import { useAsync } from '@react-hook/async';
import { Context, useContext } from 'react';

export const useRequiredContext = <T>(context: Context<T>): NonNullable<T> => {
  const value = useContext(context);

  if (value == null) {
    throw new Error(`Context "${context.displayName ?? 'unknown'}" not set`);
  }

  return value;
};

interface AsyncWrapperResult<
  ValueType extends any = any,
  ErrorType extends any = any,
  Args extends any[] = any[]
> {
  trigger: (...args: Args) => Promise<void>;
  error: ErrorType | undefined;
  data: ValueType | undefined;
  isLoading: boolean;
}

export const useAsyncWrapper = <
  ValueType extends any = any,
  ErrorType extends any = any,
  Args extends any[] = any[]
>(
  callback: (...args: Args) => Promise<ValueType>
): AsyncWrapperResult<ValueType, ErrorType, Args> => {
  const [state, trigger] = useAsync<ValueType, ErrorType, Args>(callback);

  return {
    trigger,
    error: state.error,
    data: state.value,
    isLoading: state.status === 'loading',
  };
};
